import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  useTheme,
  CircularProgress, Typography, Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useNoticeStore } from 'store/useNoticeStore';
import { getNoticesAPI } from 'apis/notice/noticeAPI';
import { useTranslation } from 'react-i18next';
import CampaignIcon from '@mui/icons-material/Campaign';
import useUserStore from 'store/useUserStore';

// 공지사항 개별 항목 타입
export interface Notice {
  id: number;
  title: string;
  content: string;
  createdAt: string; // ISO-8601 형식의 날짜 문자열
  isFixed: boolean;
  isPopup: boolean;
  viewCount: number;
}

// 공지사항 API 응답 타입
export interface NoticesResponse {
  notices: Notice[]; // 공지사항 배열
  totalPages: number; // 총 페이지 수
  totalElements: number; // 총 공지사항 수
}

export default function NoticeBoard() {
  const [noticesResponse, setNoticesResponse] = useState<NoticesResponse | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1); // 페이지네이션
  const [isAdmin, setIsAdmin] = useState(false); // 관리자 여부 (팝업 Chip에 사용됨)
  const navigate = useNavigate();
  const setSelectedNotice = useNoticeStore((state) => state.setSelectedNotice);
  // 다국어
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  // 다크 라이트 모드
  const theme = useTheme();
  //
  const { user } = useUserStore(); // 로그인 한 사람의 정보 (isAdmin에 사용됨)
  // console.log('user', user);
  // 오늘 날짜
  const today = new Date().toISOString().split('T')[0];

  // 공지사항 불러오기
  const fetchNotices = async () => {
    try {
      const response = await getNoticesAPI({ page: currentPage });
      // console.log('response.data.data', response.data);
      setNoticesResponse(response.data);
    } catch (error) {
      console.error('Error fetching notices:', error);
    }
  };

  // 공지사항 클릭 시 상세 페이지로 이동
  const handleNoticeClick = (notice: Notice) => {
    // console.log('클릭한 notice', notice);
    const updatedNotice = { ...notice, isAdmin: isAdmin };
    setSelectedNotice(updatedNotice);
    navigate(`/notice/${notice.id}`);
  };

  // 새 공지사항 작성 페이지 이동
  const handleNewNoticeClick = () => {
    navigate('/notice/new');
  };

  // 페이지 변경 핸들러
  const handlePageChange = async (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value); // 페이지 변경 시 현재 페이지 업데이트
    const response = await getNoticesAPI({ page: value });
    // console.log('handlePageChange response.data.data', response.data);
    setNoticesResponse(response.data);
  };

  // 언어 바뀔때, 공지사항 불러오기
  useEffect(() => {
    if (user?.groups.some((group) => group.type === 'SYSTEM_ADMIN')) {
      setIsAdmin(true);
    }
    fetchNotices();
  }, [i18n.language]);

  // 중요 공지사항 정렬
  const sortedNotices = noticesResponse?.notices.sort((a, b) => (a.isFixed === b.isFixed ? 0 : a.isFixed ? -1 : 1)) || [];

  return (
    <Box p={2}>
      <Box
        borderRadius={1}
        display="flex"
        flexDirection="row"
        gap={2}
        marginBottom={2}
      >
        <Typography variant="h5" fontWeight="bold">
          {t('menu.notice')}
        </Typography>

        {isAdmin && (
          <Button variant="contained" size="small" onClick={handleNewNoticeClick} color="primary" sx={{ borderRadius: '4px' }}>
            {t('notice.board.new_btn')}
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="8%">{t('notice.board.table.number')}</TableCell>
              <TableCell>{t('notice.board.table.title')}</TableCell>
              <TableCell width="15%">{t('notice.board.table.date')}</TableCell>
              <TableCell width="8%">{t('notice.board.table.count')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedNotices && sortedNotices.length > 0
              ? sortedNotices.map((notice) => (
              <TableRow key={notice.id} sx={{}}>
                {/* 중요 공지 여부 */}
                <TableCell sx={{ paddingY: '12px', textAlign: 'center', alignContent: 'center', color: theme.palette.fieldText }}>
                  {notice.isFixed ? (
                    <Chip size="small" color="primary" icon={<CampaignIcon />} sx={{ '& .MuiChip-label': { padding: '0px 4px', scale: '0.9' } }} />
                  ) : (
                    notice.id
                  )}
                </TableCell>

                {/* 제목 */}
                <TableCell
                  onClick={() => handleNoticeClick(notice)}
                  sx={{
                    paddingY: '12px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: theme.palette.fieldText,
                    // height: '40px', // 셀 높이 고정
                    '&:hover': {
                      color: theme.palette.fieldText.linkHover,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {notice.title}
                  {/* 오늘 작성된 공지사항 여부 */}
                  {notice.createdAt.split('T')[0] === today && <Chip label="N" size="small" color="error" variant="outlined" sx={{ ml: 1, scale: '0.8' }} />}
                  {isAdmin && notice.isPopup && (
                    <Chip label={t('notice.board.pop_up')} size="small" color="success" variant="outlined" sx={{ ml: 1, scale: '0.9' }} />
                  )}
                </TableCell>

                {/* 작성일 */}
                <TableCell sx={{ paddingY: '12px', color: theme.palette.fieldText }}>{notice.createdAt.split('T')[0]}</TableCell>

                {/* 조회수 */}
                <TableCell sx={{ paddingY: '12px', color: theme.palette.fieldText }}>{notice.viewCount}</TableCell>
              </TableRow>
            ))
              : <TableRow><TableCell colSpan={4} align="center" sx={{ height: '400px' }}></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      {noticesResponse?.totalElements
        ? (
        <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={Math.ceil(noticesResponse?.totalElements / 10)} // totalPages가 undefined일 경우 안전한 값
            page={currentPage}
            onChange={handlePageChange}
            showFirstButton
            showLastButton
            size="medium" // small 대신 medium으로 변경
            sx={{ padding: '8px' }} // height 제거 및 확인용 스타일
          />
        </Box>
      )
      : <></>}
    </Box>
  );
}
